/**
 * Converts a hex color string with optional transparency suffix to a proper hex color with alpha channel.
 *
 * @param input - Hex color string. The last two characters can be a transparency percentage (00-99).
 *                Examples: "#FF0000" (100% opacity), "#FF000050" (50% opacity), "#FF000099" (1% opacity)
 * @returns Hex color string with alpha channel (8 characters + #)
 *
 * @example
 * getTransparentHex("#FF0000") // returns "#FF0000FF" (100% opacity)
 * getTransparentHex("#FF000050") // returns "#FF000080" (50% opacity)
 * getTransparentHex("#FF000099") // returns "#FF000003" (1% opacity)
 */
export function getTransparentHex(
    colorHex: string,
    transparencyPercent: number,
): string {
    // Ensure color hex is 6 characters (RGB)
    if (colorHex.length !== 7) {
        throw new Error(
            `Invalid hex color format: expected 6 characters for RGB, got ${colorHex.length}`,
        )
    }

    // Convert transparency percentage (0-99) to opacity percentage (100-1)
    // 0% transparency = 100% opacity = FF
    // 99% transparency = 1% opacity = ~03
    const opacityPercent = transparencyPercent ?? 100

    // Convert opacity percentage to hex alpha (0-255)
    const alphaDecimal = Math.round((opacityPercent / 100) * 255)
    const alphaHex = alphaDecimal.toString(16).padStart(2, '0').toUpperCase()

    return `${colorHex}${alphaHex}`
}
